// src/utils/financePath.js
//
// WHY THIS EXISTS:
// Petty Cash, Bank Accounts, Bank Transactions, Receivables, Payables and
// Credit Management Redux actions are SHARED across all company types
// (manufacturer / wholesaler / retailer / restaurant / distributor).
//
// For manufacturer companies, these modules must go through the new
// permission-gated routes mounted at /manufacturer/finance/... (see
// backend src/routes/manufacturerFinance.js), so that Owner ceiling +
// role-defaults + user-overrides can control access.
//
// For every other company type, nothing changes — they keep hitting the
// original shared endpoints (/petty-cash, /bank/..., /credit/...,
// /acc-rec-pay/...) exactly as before.
//
// financePath() is a tiny router: give it the "shared" path (the one the
// action file already calls) and it returns either that same path
// unchanged, or the equivalent manufacturer path, based on the logged-in
// user's company_type (read from the JWT via getAuthUser()).
//
// USAGE:
//   import { financePath } from '../../utils/financePath';
//   const response = await axiosInstance.get(financePath('petty-cash'));
//   const response = await axiosInstance.post(financePath('bank/bank-accounts'), data);
//
// The mapping table below lists every shared prefix this helper knows
// about. Add a new row here whenever a new finance sub-module needs the
// same manufacturer/shared split.

import { getAuthUser } from './auth';
import { isManufacturerFamily } from 'utils/companyTypes';

// Ordered so longer/more-specific prefixes are checked before shorter ones
// (e.g. "bank/transactions" before "bank").
const PREFIX_MAP = [
  // Petty cash balance routes (daily-flow, current, set-opening) manufacturer par
  // '/petty-cash-balance/...' naam se hain, isliye 'petty-cash' se pehle check hota hai.
  ['petty-cash/balance', 'manufacturer/finance/petty-cash-balance'],
  ['petty-cash', 'manufacturer/finance/petty-cash'],
  // Sub Petty Cash is manufacturer-only (no shared /sub-petty-cash route
  // exists at all for other company types), but routed through financePath()
  // anyway for consistency with the rest of this file / the SubPettyCashAction
  // import pattern.
  ['sub-petty-cash', 'manufacturer/finance/sub-petty-cash'],
  ['bank/bank-accounts', 'manufacturer/finance/bank-accounts'],
  ['bank/transactions', 'manufacturer/finance/bank-transactions'],
  ['acc-rec-pay/receivables', 'manufacturer/finance/receivables'],
  ['acc-rec-pay/payables', 'manufacturer/finance/payables'],
  ['credit', 'manufacturer/finance/credit'],
  ['product-categories', 'manufacturer/categories'],
  ['clients', 'manufacturer/clients'],
  ['purchases', 'manufacturer/purchases'],
  ['grn', 'manufacturer/grn'],
];

/**
 * Rewrites a shared finance API path to its manufacturer-gated equivalent
 * when the logged-in user belongs to a manufacturer company. For every
 * other company_type, the path is returned unchanged.
 *
 * @param {string} sharedPath - the path as used by the shared route
 *   (e.g. 'petty-cash/summary', 'bank/bank-accounts/12', 'credit/parties')
 * @returns {string} the path to actually call
 */
export const financePath = (sharedPath) => {
  const { company_type } = getAuthUser();

  if (!isManufacturerFamily(company_type)) {
    return sharedPath;
  }

  for (const [sharedPrefix, manufacturerPrefix] of PREFIX_MAP) {
    if (sharedPath === sharedPrefix || sharedPath.startsWith(`${sharedPrefix}/`) || sharedPath.startsWith(`${sharedPrefix}?`)) {
      return manufacturerPrefix + sharedPath.slice(sharedPrefix.length);
    }
  }

  // Unknown prefix — fall back to the shared path rather than guessing.
  return sharedPath;
};

export const isManufacturer = () => isManufacturerFamily(getAuthUser().company_type);

// Retailer POS module — same pattern as isManufacturer() above. Used to
// gate retailer-only UI (e.g. product image upload in ProductForm.jsx)
// so wholesaler/restaurant/manufacturer/distributor company types never
// see it, per the Retailer POS spec's scope rule.
export const isRetailer = () => getAuthUser().company_type === 'retailer';

// Alias — same function, generic name. financePath() name is kept above
// for backward compatibility with existing Finance action-file imports;
// new non-finance modules (categories, clients, purchases) should import
// manufacturerPath() instead since the PREFIX_MAP now covers more than
// just Finance.
export const manufacturerPath = financePath;